import { ENDPOINT } from '@/services/endpoint';
import { InquiryService } from '@/services/inquiry';
import { useInquiryStore } from '@/store';
import { IInquiry, InquiryStatus } from '@/types/response/inquiry';
import { inquiryStatus, inquiryStatusColors } from '@/utils';
import { Button, Card, Drawer, Radio, Spin, Tag } from 'antd';
import moment from 'moment';
import { FC, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { CopyOutlined, LoadingOutlined } from '@ant-design/icons';

interface DescriptionItemProps {
  title: string;
  content: string | number;
  allowCopy?: boolean;
}

const DescriptionItem = ({
  title,
  content,
  allowCopy = false,
}: DescriptionItemProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(content));
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 500);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };
  return (
    <div className='grid grid-cols-6 items-center w-full'>
      <div className='col-span-2 text-md font-bold'>{title}</div>
      <div className='col-span-4 text-md flex items-center gap-2'>
        <span>{content}</span>
        {content && allowCopy && (
          <Button
            size='small'
            type='text'
            icon={
              isCopied ? (
                <LoadingOutlined spin width={20} />
              ) : (
                <CopyOutlined className='text-sm' />
              )
            }
            onClick={handleCopy}
            className='py-0'
          />
        )}
      </div>
    </div>
  );
};

const InquiryDetailContent: FC<{ inquiry: IInquiry }> = ({ inquiry }) => {
  const qc = useQueryClient();

  const [status, setStatus] = useState(inquiry.status);

  const { mutate: updateStatus, isLoading } = useMutation({
    mutationFn: InquiryService.updateStatus,
    onSuccess: (data) => {
      qc.setQueryData([ENDPOINT.INQUIRY.GET_DETAIL, { id: inquiry.id }], data);
      qc.invalidateQueries([ENDPOINT.INQUIRY.GET_LIST]);
    },
  });

  return (
    <Spin spinning={isLoading}>
      <h3 className='text-center text-3xl font-bold mb-6'>お問い合わせ内容</h3>
      <Card>
        <div className='flex flex-col gap-2'>
          {/* <DescriptionItem title='ID' content={inquiry.id} /> */}
          <DescriptionItem
            title='ユーザーID'
            content={inquiry.createdBy?.id ?? ''}
            allowCopy
          />
          <DescriptionItem
            title='問い合わせ日時'
            content={
              inquiry.createdAt
                ? moment(inquiry.createdAt).format('YYYY年MM月DD日 HH:mm')
                : ''
            }
          />
          <DescriptionItem title='お名前' content={inquiry.name} allowCopy />
          <DescriptionItem
            title='メールアドレス'
            content={inquiry.email}
            allowCopy
          />
          <DescriptionItem
            title='本登録の有無'
            content={inquiry.isRegistered ? '本登録済み' : '未登録'}
          />
          <DescriptionItem title='端末名' content={inquiry.deviceName} />
          <DescriptionItem title='OS Version' content={inquiry.osVersion} />
          <DescriptionItem title='App Version' content={inquiry.appVersion} />
          <div className='flex flex-col gap-2'>
            <div className='col-span-2 text-md font-bold'>お問い合わせ内容</div>
            <Card className='bg-white whitespace-pre-wrap'>
              {inquiry.content}
            </Card>
          </div>
          <div className='flex flex-col gap-2'>
            <div className='col-span-2 text-md font-bold'>ステータス </div>
            <Radio.Group
              value={status}
              onChange={({ target: { value } }) => {
                setStatus(value);
                updateStatus({ id: inquiry.id, status: value });
              }}
              options={[
                {
                  label: (
                    <Tag color={inquiryStatusColors[InquiryStatus.NOT_ANSWER]}>
                      {inquiryStatus[InquiryStatus.NOT_ANSWER]}
                    </Tag>
                  ),
                  value: InquiryStatus.NOT_ANSWER,
                },
                {
                  label: (
                    <Tag color={inquiryStatusColors[InquiryStatus.ANSWERED]}>
                      {inquiryStatus[InquiryStatus.ANSWERED]}
                    </Tag>
                  ),
                  value: InquiryStatus.ANSWERED,
                },
              ]}
            />
          </div>
        </div>
      </Card>
    </Spin>
  );
};

export const InquiryDetail = () => {
  const { id, openDetail, onCloseDetail } = useInquiryStore();

  const { isLoading, data } = useQuery({
    queryKey: [ENDPOINT.INQUIRY.GET_DETAIL, { id }],
    queryFn: () => InquiryService.getDetail(id!),
    enabled: !!id,
    select: (res) => res.data,
  });

  return (
    <Drawer
      width={600}
      placement='right'
      closable={false}
      onClose={onCloseDetail}
      open={openDetail}
      loading={isLoading}
    >
      {data ? <InquiryDetailContent inquiry={data} /> : null}
    </Drawer>
  );
};
